import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  FormHelperText,
  Paper,
  Divider,
  Dialog,
  DialogContent,
  DialogActions,
  Tabs,
  Tab
} from '@mui/material';
import { Save, Cancel, Business, Person, Language, CheckCircle, Error as ErrorIcon, ArrowBack, PersonAdd, PersonSearch } from '@mui/icons-material';
import { Layout } from '@layout/Layout';
import { useTheme } from '@mui/material/styles';
import { useAppTheme } from '@contexts/ThemeContext';
import { useOrgAdminStore } from '@store/orgAdminStore';
import {
  User,
  CreateOrgAdminRequest,
  UpdateOrgAdminRequest,
  OrgAdmin
} from '@services/OrgAdminService';

interface FormData {
  userId: string;
  organizationName: string;
  domains: string[];
  status: 'active' | 'inactive';
}

interface NewUserFormData {
  name: string;
  email: string;
  organizationName: string;
  domains: string[];
}

const CreateOrgAdmin: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { isDarkMode } = useAppTheme();

  // Get editing admin from location state if exists
  const editingAdmin = location.state?.admin as OrgAdmin | undefined;
  const isEditMode = !!editingAdmin;

  // Zustand store
  const {
    users,
    domains,
    loading,
    error,
    success,
    fetchAllUsers,
    fetchAllDomains,
    createAdmin,
    createUserAndAdmin,
    updateAdmin,
    clearMessages,
  } = useOrgAdminStore();

  // Tab state (0 = existing user, 1 = new user)
  const [tabValue, setTabValue] = useState(0);

  // Existing user form state
  const [formData, setFormData] = useState<FormData>({
    userId: editingAdmin?.user_id || '',
    organizationName: editingAdmin?.organization_name || '',
    domains: editingAdmin?.domains?.map(d => d.domain) || [],
    status: editingAdmin?.status || 'active'
  });
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof FormData, string>>>({});

  // New user form state
  const [newUserFormData, setNewUserFormData] = useState<NewUserFormData>({
    name: '',
    email: '',
    organizationName: '',
    domains: []
  });
  const [newUserFormErrors, setNewUserFormErrors] = useState<Partial<Record<keyof NewUserFormData, string>>>({});

  const [messageDialog, setMessageDialog] = useState(false);

  // Load users and domains on mount
  useEffect(() => {
    fetchAllUsers();
    fetchAllDomains();
  }, [fetchAllUsers, fetchAllDomains]);

  // Set selected user in edit mode
  useEffect(() => {
    if (editingAdmin?.user_id && users.length > 0) {
      const user = users.find(u => u.userId === editingAdmin.user_id);
      if (user) {
        setSelectedUser(user);
      }
    }
  }, [editingAdmin, users]);

  // Show message modal when success or error occurs
  useEffect(() => {
    if (success || error) {
      setMessageDialog(true);
    }
  }, [success, error]);

  // Handle message modal close
  const handleMessageDialogClose = () => {
    setMessageDialog(false);
    clearMessages();

    // If success, navigate back to list page after closing modal
    if (success) {
      navigate('/manage-organizational-admins');
    }
  };

  // Validate existing user form
  const validateForm = (): boolean => {
    const errors: Partial<Record<keyof FormData, string>> = {};

    if (!formData.userId) {
      errors.userId = 'User is required';
    }
    if (!formData.organizationName.trim()) {
      errors.organizationName = 'Organization name is required';
    }
    if (formData.domains.length === 0) {
      errors.domains = 'At least one domain is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Validate new user form
  const validateNewUserForm = (): boolean => {
    const errors: Partial<Record<keyof NewUserFormData, string>> = {};

    if (!newUserFormData.name.trim()) {
      errors.name = 'Name is required';
    }
    if (!newUserFormData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newUserFormData.email)) {
      errors.email = 'Invalid email format';
    }
    if (!newUserFormData.organizationName.trim()) {
      errors.organizationName = 'Organization name is required';
    }
    if (newUserFormData.domains.length === 0) {
      errors.domains = 'At least one domain is required';
    }

    setNewUserFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle existing user form submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    clearMessages();

    if (isEditMode && editingAdmin) {
      // Update existing admin
      const updateData: UpdateOrgAdminRequest = {
        organizationName: formData.organizationName,
        status: formData.status,
        domains: formData.domains
      };

      await updateAdmin(editingAdmin.id, updateData);
    } else {
      // Create new admin
      const createData: CreateOrgAdminRequest = {
        userId: formData.userId,
        organizationName: formData.organizationName,
        domains: formData.domains
      };

      await createAdmin(createData);
    }
    // Message modal will show automatically via useEffect
  };

  // Handle new user form submit
  const handleNewUserSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateNewUserForm()) return;

    clearMessages();

    // Call API to create user + org admin
    await createUserAndAdmin(newUserFormData);
    // Message modal will show automatically via useEffect
  };

  return (
    <Layout>
      <Box sx={{ p: { xs: 2, sm: 3 }, width: '100%' }}>
        {/* Back Button */}
        <Box mb={2}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate(-1)}
            variant="text"
            sx={{ color: 'text.secondary' }}
          >
            Back
          </Button>
        </Box>

        {/* Header */}
        <Box mb={3}>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            {isEditMode ? 'Edit Organizational Admin' : 'Create Organizational Admin'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isEditMode
              ? 'Update the organizational admin details below'
              : 'Create a new organizational admin by filling out the form below'
            }
          </Typography>
        </Box>

        {/* Form Card */}
        <Card elevation={2} className="create-org-admin-card">
          <CardContent sx={{ p: 0 }}>
            {/* Tabs - Only show in create mode */}
            {!isEditMode && (
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs
                  value={tabValue}
                  onChange={(e, newValue) => setTabValue(newValue)}
                  aria-label="user selection tabs"
                  className="create-org-admin-tabs"
                >
                  <Tab
                    icon={<PersonSearch />}
                    iconPosition="start"
                    label="Select Existing User"
                    className="create-org-admin-tab-existing"
                  />
                  <Tab
                    icon={<PersonAdd />}
                    iconPosition="start"
                    label="Create New Admin"
                    className="create-org-admin-tab-new"
                  />
                </Tabs>
              </Box>
            )}

            <Box sx={{ p: 4 }}>
              {/* Tab Panel 0 - Existing User */}
              {(isEditMode || tabValue === 0) && (
                <form onSubmit={handleSubmit} className="create-org-admin-existing-form">
              <Box display="flex" flexDirection="column" gap={3}>

                {/* User Selection - Searchable Autocomplete */}
                {!isEditMode && (
                  <Box>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Person sx={{ color: isDarkMode ? theme.palette.info.light : theme.palette.primary.main }} />
                      <Typography variant="subtitle1" fontWeight={600}>
                        Select User *
                      </Typography>
                    </Box>
                    <Autocomplete
                      fullWidth
                      options={users}
                      value={selectedUser}
                      onChange={(event, newValue) => {
                        if (newValue) {
                          setSelectedUser(newValue);
                          setFormData((prev) => ({
                            ...prev,
                            userId: newValue.userId
                          }));
                          setFormErrors((prev) => ({ ...prev, userId: undefined }));
                        } else {
                          setSelectedUser(null);
                          setFormData((prev) => ({
                            ...prev,
                            userId: ''
                          }));
                        }
                      }}
                      getOptionLabel={(option) => `${option.name} (${option.email})`}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Search by name or email..."
                          error={!!formErrors.userId}
                          helperText={formErrors.userId}
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props} key={option.userId}>
                          <Box>
                            <Typography variant="body2" fontWeight={500}>
                              {option.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {option.email}
                            </Typography>
                          </Box>
                        </li>
                      )}
                      isOptionEqualToValue={(option, value) => option.userId === value.userId}
                      noOptionsText="No users found"
                    />
                  </Box>
                )}

                {/* Show selected user in edit mode */}
                {isEditMode && editingAdmin && (
                  <Box>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Person sx={{ color: isDarkMode ? theme.palette.info.light : theme.palette.primary.main }} />
                      <Typography variant="subtitle1" fontWeight={600}>
                        Admin User
                      </Typography>
                    </Box>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                      <Typography variant="body2" fontWeight={500}>
                        {editingAdmin.user_name || 'N/A'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {editingAdmin.user_email || 'N/A'}
                      </Typography>
                    </Paper>
                  </Box>
                )}

                <Divider />

                {/* Organization Name */}
                <Box>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Business sx={{ color: isDarkMode ? theme.palette.info.light : theme.palette.primary.main }} />
                    <Typography variant="subtitle1" fontWeight={600}>
                      Organization Name *
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    placeholder="Enter organization name"
                    value={formData.organizationName}
                    onChange={(e) => {
                      setFormData((prev) => ({ ...prev, organizationName: e.target.value }));
                      setFormErrors((prev) => ({ ...prev, organizationName: undefined }));
                    }}
                    error={!!formErrors.organizationName}
                    helperText={formErrors.organizationName}
                  />
                </Box>

                {/* Status - Only for edit mode */}
                {isEditMode && (
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600} mb={1}>
                      Status
                    </Typography>
                    <FormControl fullWidth>
                      <Select
                        value={formData.status}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            status: e.target.value as 'active' | 'inactive'
                          }))
                        }
                      >
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="inactive">Inactive</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                )}

                <Divider />

                {/* Domains */}
                <Box>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Language sx={{ color: isDarkMode ? theme.palette.info.light : theme.palette.primary.main }} />
                    <Typography variant="subtitle1" fontWeight={600}>
                      Allowed Domains *
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    Select email domains that are allowed for this organization
                  </Typography>
                  <Typography variant="caption" color="text.secondary" mb={2} display="block">
                    Search and select from existing domains
                  </Typography>

                  <Autocomplete
                    multiple
                    fullWidth
                    options={domains.map(d => d.domain)}
                    value={formData.domains}
                    onChange={(event, newValue) => {
                      setFormData((prev) => ({ ...prev, domains: newValue }));
                      setFormErrors((prev) => ({ ...prev, domains: undefined }));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Search and select domains..."
                        error={!!formErrors.domains}
                        helperText={formErrors.domains}
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          label={option}
                          {...getTagProps({ index })}
                          color="info"
                          variant="filled"
                          sx={{
                            fontWeight: 500,
                            '& .MuiChip-label': {
                              px: 1.5
                            }
                          }}
                        />
                      ))
                    }
                    noOptionsText="No domains available"
                  />
                </Box>

                <Divider />

                {/* Action Buttons */}
                <Box display="flex" gap={2} justifyContent="flex-end" pt={2}>
                  <Button
                    variant="outlined"
                    startIcon={<Cancel />}
                    onClick={() => navigate('/manage-organizational-admins')}
                    disabled={loading}
                    size="large"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                    disabled={loading}
                    size="large"
                  >
                    {loading ? 'Saving...' : (isEditMode ? 'Update Admin' : 'Create Admin')}
                  </Button>
                </Box>
              </Box>
            </form>
          )}

          {/* Tab Panel 1 - New User */}
          {!isEditMode && tabValue === 1 && (
            <form onSubmit={handleNewUserSubmit} className="create-org-admin-new-user-form">
              <Box display="flex" flexDirection="column" gap={3}>

                {/* New User Name */}
                <Box className="create-org-admin-name-field">
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Person sx={{ color: isDarkMode ? theme.palette.info.light : theme.palette.primary.main }} />
                    <Typography variant="subtitle1" fontWeight={600}>
                      Full Name *
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    placeholder="Enter full name"
                    value={newUserFormData.name}
                    onChange={(e) => {
                      setNewUserFormData((prev) => ({ ...prev, name: e.target.value }));
                      setNewUserFormErrors((prev) => ({ ...prev, name: undefined }));
                    }}
                    error={!!newUserFormErrors.name}
                    helperText={newUserFormErrors.name}
                  />
                </Box>

                {/* New User Email */}
                <Box className="create-org-admin-email-field">
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Language sx={{ color: isDarkMode ? theme.palette.info.light : theme.palette.primary.main }} />
                    <Typography variant="subtitle1" fontWeight={600}>
                      Email Address *
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    type="email"
                    placeholder="Enter email address"
                    value={newUserFormData.email}
                    onChange={(e) => {
                      setNewUserFormData((prev) => ({ ...prev, email: e.target.value }));
                      setNewUserFormErrors((prev) => ({ ...prev, email: undefined }));
                    }}
                    error={!!newUserFormErrors.email}
                    helperText={newUserFormErrors.email || 'A temporary password will be sent to this email'}
                  />
                </Box>

                <Divider />

                {/* Organization Name */}
                <Box className="create-org-admin-org-name-field">
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Business sx={{ color: isDarkMode ? theme.palette.info.light : theme.palette.primary.main }} />
                    <Typography variant="subtitle1" fontWeight={600}>
                      Organization Name *
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    placeholder="Enter organization name"
                    value={newUserFormData.organizationName}
                    onChange={(e) => {
                      setNewUserFormData((prev) => ({ ...prev, organizationName: e.target.value }));
                      setNewUserFormErrors((prev) => ({ ...prev, organizationName: undefined }));
                    }}
                    error={!!newUserFormErrors.organizationName}
                    helperText={newUserFormErrors.organizationName}
                  />
                </Box>

                <Divider />

                {/* Domains */}
                <Box className="create-org-admin-domains-field">
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Language sx={{ color: isDarkMode ? theme.palette.info.light : theme.palette.primary.main }} />
                    <Typography variant="subtitle1" fontWeight={600}>
                      Allowed Domains *
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    Select email domains that are allowed for this organization
                  </Typography>
                  <Typography variant="caption" color="text.secondary" mb={2} display="block">
                    Search and select from existing domains
                  </Typography>

                  <Autocomplete
                    multiple
                    fullWidth
                    options={domains.map(d => d.domain)}
                    value={newUserFormData.domains}
                    onChange={(event, newValue) => {
                      setNewUserFormData((prev) => ({ ...prev, domains: newValue }));
                      setNewUserFormErrors((prev) => ({ ...prev, domains: undefined }));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Search and select domains..."
                        error={!!newUserFormErrors.domains}
                        helperText={newUserFormErrors.domains}
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          label={option}
                          {...getTagProps({ index })}
                          color="info"
                          variant="filled"
                          sx={{
                            fontWeight: 500,
                            '& .MuiChip-label': {
                              px: 1.5
                            }
                          }}
                        />
                      ))
                    }
                    noOptionsText="No domains available"
                  />
                </Box>

                <Divider />

                {/* Info Alert */}
                <Alert severity="info" className="create-org-admin-info-alert">
                  <Typography variant="body2">
                    A new user will be created with admin privileges (rollId = 1).
                    An email will be sent to the provided email address with login credentials and a temporary password.
                  </Typography>
                </Alert>

                {/* Action Buttons */}
                <Box display="flex" gap={2} justifyContent="flex-end" pt={2} className="create-org-admin-actions">
                  <Button
                    variant="outlined"
                    startIcon={<Cancel />}
                    onClick={() => navigate('/manage-organizational-admins')}
                    disabled={loading}
                    size="large"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                    disabled={loading}
                    size="large"
                  >
                    {loading ? 'Creating...' : 'Create User & Admin'}
                  </Button>
                </Box>
              </Box>
            </form>
          )}
            </Box>
          </CardContent>
        </Card>

        {/* Success/Error Message Dialog */}
        <Dialog
          open={messageDialog}
          onClose={handleMessageDialogClose}
          maxWidth="sm"
          fullWidth
        >
          <DialogContent sx={{ textAlign: 'center', py: 4 }}>
            {success && (
              <>
                <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Success!
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {success}
                </Typography>
              </>
            )}
            {error && (
              <>
                <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Error
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {error}
                </Typography>
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
            <Button
              variant="contained"
              onClick={handleMessageDialogClose}
              sx={{ minWidth: 120 }}
            >
              OK
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
};

export default CreateOrgAdmin;
